import { useState } from 'react';
import { App, Button, Card, Input, InputNumber, Select, Space, Switch, Tag, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { PageHero } from '@/components/PageHero';
import { useSettings, useUpdateSetting } from '@/hooks/useSettings';
import type { RuntimeSetting, SettingValue } from '@/types/setting';

const SOURCE_COLOR: Record<string, string> = { db: 'green', env: 'blue', default: 'default' };

export function SystemSettingsPage() {
  const q = useSettings();
  const settings = q.data ?? [];
  const groups = [...new Set(settings.map((s) => s.group))];
  return (
    <div style={{ maxWidth: 900 }}>
      <PageHero icon={<SettingOutlined />} title="System Settings" subtitle="Runtime knobs — super-admin only" />
      {groups.map((g) => (
        <Card key={g} size="small" title={g} style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {settings.filter((s) => s.group === g).map((s) => <SettingRow key={s.key} s={s} />)}
          </Space>
        </Card>
      ))}
    </div>
  );
}

function SettingRow({ s }: { s: RuntimeSetting }) {
  const { message } = App.useApp();
  const update = useUpdateSetting();
  const [val, setVal] = useState<SettingValue>(s.value);
  const dirty = val !== s.value;
  const save = () =>
    update.mutate({ key: s.key, value: val }, {
      onSuccess: () => message.success('Saved'),
      onError: (e) => message.error((e as Error).message),
    });
  return (
    <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
      <div style={{ maxWidth: 520 }}>
        <Space size={6}>
          <Typography.Text strong>{s.label}</Typography.Text>
          <Tag color={SOURCE_COLOR[s.source]}>{s.source}</Tag>
        </Space>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '2px 0 0' }}>
          {s.description}
          {s.type === 'int' && s.min != null ? ` (${s.min}–${s.max})` : ''}
        </Typography.Paragraph>
      </div>
      <Space>
        <SettingControl s={s} val={val} onChange={setVal} />
        <Button type="primary" size="small" disabled={!dirty} loading={update.isPending} onClick={save}>Save</Button>
      </Space>
    </Space>
  );
}

function SettingControl({ s, val, onChange }: { s: RuntimeSetting; val: SettingValue; onChange: (v: SettingValue) => void }) {
  if (s.type === 'int')
    return <InputNumber min={s.min ?? undefined} max={s.max ?? undefined} value={val as number} onChange={(v) => onChange(v ?? (s.default as number))} />;
  if (s.type === 'bool') return <Switch checked={val as boolean} onChange={onChange} />;
  if (s.type === 'enum')
    return <Select style={{ width: 160 }} value={val as string} options={(s.options ?? []).map((o) => ({ value: o }))} onChange={onChange} />;
  return <Input style={{ width: 220 }} value={val as string} onChange={(e) => onChange(e.target.value)} />;
}
